import { FC, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { graphql, useFragment } from 'react-relay';
import { useAuctionPricing } from 'dibs-auctions/exports/hooks';

// components
import { FormattedPrice } from 'dibs-buyer-price';
import { FormattedMessage } from 'dibs-react-intl';
import { PdpSharedPurchaseButton } from '../PdpSharedButtons/PdpSharedPurchaseButton';
import { PdpSharedAuctionViewHistory } from '../PdpSharedAuctionViewHistory/PdpSharedAuctionViewHistory';
import { PdpSharedAuctionMessageContainer } from './PdpSharedAuctionMessageContainer';

// types
import { PdpSharedAuctionActionCompletePurchase_item$key as Item } from './__generated__/PdpSharedAuctionActionCompletePurchase_item.graphql';

// styles
import styles from './PdpSharedAuctionActionCompletePurchase.scss';

type Props = {
    item: Item;
    currency: string;
};

type RelayVariables = {
    isClient: boolean;
    userId: string;
    itemId: string;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

const itemFragment = graphql`
    fragment PdpSharedAuctionActionCompletePurchase_item on Item {
        isSold
        lastTransaction(buyerId: $userId) @include(if: $fetchUser) {
            checkoutDate
        }
        ...useAuctionPricing_item @arguments(page: pdp)
        ...PdpSharedPurchaseButton_item
    }
`;

const completePurchaseLabel = (
    <FormattedMessage
        id="PdpSharedAuctionActionCompletePurchase.completePurchase"
        defaultMessage="Complete Purchase"
    />
);

export const PdpSharedAuctionActionCompletePurchase: FC<Props> = ({ item: itemRef, currency }) => {
    const item = useFragment(itemFragment, itemRef);
    const { currentBidAmount } = useAuctionPricing({ item, currency });
    const { userId, itemId } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );
    const { isSold, lastTransaction } = item || {};
    const { checkoutDate } = lastTransaction || {};

    return (
        <div className={styles.container}>
            <span className={styles.closed} data-tn="pdp-bidding-closed">
                <FormattedMessage
                    id="PdpSharedAuctionActionCompletePurchase.biddingClosed"
                    defaultMessage="Bidding Closed"
                />
            </span>
            <div className={styles.congratulationsContainer}>
                <PdpSharedAuctionMessageContainer
                    text={
                        <span data-tn="pdp-auction-won-message">
                            <FormattedMessage
                                id="PdpSharedAuctionActionCompletePurchase.congratulations"
                                defaultMessage="<b>Congratulations!</b> You won this item."
                                values={{
                                    b: (chunks: ReactNode[]) => (
                                        <span className={styles.bold}>{chunks}</span>
                                    ),
                                }}
                            />
                        </span>
                    }
                />
            </div>
            <span className={styles.bold}>
                <FormattedMessage
                    id="PdpSharedAuctionActionCompletePurchase.winningBid"
                    defaultMessage="Your Winning Bid"
                />
            </span>
            {currentBidAmount && (
                <span className={styles.price} data-tn="pdp-winning-bid-amount">
                    <FormattedPrice value={currentBidAmount} currency={currency} />
                </span>
            )}
            {!(isSold || checkoutDate) && (
                <div className={styles.purchase}>
                    <PdpSharedPurchaseButton
                        item={item}
                        itemId={itemId}
                        userId={userId}
                        quantitySelected={'1'}
                        label={completePurchaseLabel}
                    />
                </div>
            )}
            <PdpSharedAuctionViewHistory currency={currency} />
        </div>
    );
};
